import { useEffect, useState } from 'react';
import { Table, Button, Row, Col, Input, Tooltip, Alert } from 'antd';
import { CensusDto, NoteDto, EnumSelectListDto, FacilityTypes, AwvEligibilityStatus } from '@medone/medonehp-api-client';
import moment from 'moment';
import { uniq } from 'lodash';

import { togglePatientDrawer } from '../slice.patients';
import { selectNote } from '../slice.notes';
import { formatDateTime, isMobileView, getEnumFilters, isInRole } from '../../../../../shared/common/helpers';
import { useAppDispatch, useAppSelector } from '../../../../../shared/hooks';
import { sortWithNullsAtBottom } from '../../../../../shared/common/helpers/table';
import { selectRequiredVisitTypes } from '../../../../../shared/common/data/slice';
import { censusBadgeSelectors, specialtiesSelectors, visitDueSelectors } from '../slice';
import { Role, RolesAdminOrAnyProvider, SpecialtyRole } from '../../../../../shared/common/auth/RoleAuth';
import { selectAuth } from '../../../../../shared/common/auth/slice';
import { AuthState } from '../../../../../shared/common/auth/models';

import PatientTableMobile from '../components-mobile/patient-table';
import ActionButtons from './action-buttons';
import ProviderTypeBadge from './badges/provider-type';
import VisitDueBadge from './badges/visit-due';
import LastVisitedBadge from './badges/last-visited';
import CensusBadges from './badges';
import ScheduledBadge from './badges/scheduled';
import VisitTypeBadge from './badges/visit-type';
import EditableRoomNumber from './room-number';
import Skilled from './skilled';
//import NextVisitBadges from './badges/next-visit-badges';

const { Column } = Table;

type Props = {
    census: CensusDto[];
};

const PatientTable = ({ census }: Props) => {
    const dispatch = useAppDispatch();
    const auth = useAppSelector<AuthState>(selectAuth);
    const latestNote = useAppSelector<NoteDto>(selectNote);
    const requiredVisitTypes = useAppSelector<EnumSelectListDto[]>(selectRequiredVisitTypes);
    const badges = useAppSelector((state) => censusBadgeSelectors.selectAll(state.census.badges));
    const visitDues = useAppSelector((state) => visitDueSelectors.selectAll(state.census.visitDues));
    const intakeSpecialties = useAppSelector((state) => specialtiesSelectors.selectAll(state.census.specialties));
    const [dataSource, setDataSource] = useState<CensusDto[]>(census);
    const [searchLength, setSearchLength] = useState<number>(0);
    const [searchTemp, setSearchTemp] = useState<string>();

    const showRequiredVisitColumns = isInRole(auth.permissions, [
        Role.POST_ACUTE_ADMIN,
        Role.PHYSICIAN,
        Role.ADVANCED_PRACTICE_PRACTITIONER,
        Role.CLINICAL_COORDINATOR,
        SpecialtyRole.Primary,
    ]);

    useEffect(() => {
        let patientIntakeId: string | number = localStorage.getItem('patientIntakeId');
        if (patientIntakeId) {
            patientIntakeId = parseInt(patientIntakeId);

            const item = census.find((x) => x.patientIntakeId === patientIntakeId);
            if (item != null) {
                dispatch(togglePatientDrawer(item));
            }

            localStorage.removeItem('patientIntakeId');
        }

        setDataSource(census);

        if (searchTemp) {
            processSearch(null, searchTemp);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, census]);

    // const renderVisitBadges = (censusDto: CensusDto) => {
    //     // PLAN: In the future we may add this to replace the regulatory visit columns on the right side of the table
    //     return (
    //         <>
    //             <Row gutter={0}>
    //                 <Col span={24}>
    //                     <NextVisitBadges censusDto={censusDto} />
    //                 </Col>
    //             </Row>
    //         </>
    //     );
    // };

    const renderPatientName = (item: CensusDto) => {
        const isDischarged = item.dischargeDate != null;
        const showAsDischarged = isDischarged && !item.canReturnFromED;

        return (
            <>
                <Row gutter={0}>
                    <Col span={24}>
                        <Button type="link" onClick={() => dispatch(togglePatientDrawer(item))} className={showAsDischarged && 'patient-discharged'}>
                            <Tooltip title={isDischarged && `Discharged on ${formatDateTime(item.dischargeDate)}`}>{item.name}</Tooltip>
                        </Button>
                    </Col>
                </Row>

                <CensusBadges patientIntakeId={item.patientIntakeId} patientTypeIds={item.patientTypeIds} canReturnFromED={item.canReturnFromED} />
            </>
        );
    };

    const renderAWV = (item: CensusDto) => {
        const fontClass = item.awvEligibilityStatus === AwvEligibilityStatus.EligibleWithRecentQueue ? 'text-green' : '';

        return item.awvEligibilityStatus !== AwvEligibilityStatus.NotEligible && <span className={fontClass}>AWV</span>;
    };

    // Allows more granular control over search parameters.
    // For instance, allows formatting and searching of moment objects
    const processResults = (o: CensusDto, k: string, searchValue: string) => {
        if (moment.isMoment(o[k])) {
            return o[k].format('L').toLowerCase().includes(searchValue.toLowerCase());
        } else {
            return String(o[k]).toLowerCase().includes(searchValue.toLowerCase());
        }
    };

    const processSearch = (e, valueOverride: string = null) => {
        const searchValue = valueOverride ?? e.target.value;

        // If the new search term is shorter than what it was before, the user is deleting letters.
        // We need to reset the collection being searched in this case to allow for finding different
        // results from the full set.
        if (searchValue.length < searchLength) {
            setDataSource(census);
        }

        setSearchLength(searchValue.length);

        if (!valueOverride) {
            setSearchTemp(searchValue);
        }

        if (searchValue.length >= 3) {
            const filteredData = dataSource.filter((o) => Object.keys(o).some((k) => processResults(o, k, searchValue)));

            setDataSource(filteredData);
        } else if (searchValue.length === 0) {
            setDataSource(census);
        }
    };

    const renderSearch = () => {
        return (
            <Row gutter={24}>
                <Col span={isMobileView() ? 22 : 12}>
                    <Input className="census-search" placeholder="Search by name or room number" onChange={(e) => processSearch(e)} />
                </Col>
            </Row>
        );
    };

    const getProviderTypes = (): number[] => {
        return uniq(visitDues?.filter((x) => x?.providerVisitType).map((x) => x?.providerVisitType)) ?? [];
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const nextVisitSorter = (a: CensusDto, b: CensusDto) => {
        // New patient
        const a_IsNew = intakeSpecialties.find((x) => x.patientIntakeId === a.patientIntakeId)?.specialties.find((x) => x.isNew);
        const b_IsNew = intakeSpecialties.find((x) => x.patientIntakeId === b.patientIntakeId)?.specialties.find((x) => x.isNew);

        if (a_IsNew && !b_IsNew) {
            return -1;
        } else if (b_IsNew && !a_IsNew) {
            return 1;
        }

        // QuickNote Follow Up Date
        const aBadges = badges.find((x) => x.patientIntakeId === a.patientIntakeId);
        const bBadges = badges.find((x) => x.patientIntakeId === b.patientIntakeId);

        if (aBadges?.isQuickNoteFollowup && !bBadges?.isQuickNoteFollowup) {
            return 1;
        } else if (bBadges?.isQuickNoteFollowup && !aBadges?.isQuickNoteFollowup) {
            return -1;
        }

        // IsAnchored
        const a_IsAcoNotAnchored = a.isAcoPatient && a.isNotAnchored;
        const b_IsAcoNotAnchored = b.isAcoPatient && b.isNotAnchored;

        if (!a_IsAcoNotAnchored && b_IsAcoNotAnchored) {
            return 1;
        } else if (!b_IsAcoNotAnchored && a_IsAcoNotAnchored) {
            return -1;
        }

        // H&P highest number to lowest number
        const a_overDueDays = visitDues.find((x) => x.patientIntakeId === a.patientIntakeId)?.overDueInDays;
        const b_overDueDays = visitDues.find((x) => x.patientIntakeId === b.patientIntakeId)?.overDueInDays;

        if (a_overDueDays && b_overDueDays) {
            return a_overDueDays < b_overDueDays ? 1 : -1;
        } else if (a_overDueDays) {
            return -1;
        } else if (b_overDueDays) {
            return 1;
        }

        // AWV and ACO patient
        const a_IsAWVandACO = !!a.isAwvPatient && !!a.isAcoPatient;
        const b_IsAWVandACO = !!b.isAwvPatient && !!b.isAcoPatient;

        if (!a_IsAWVandACO && b_IsAWVandACO) {
            return 1;
        } else if (!b_IsAWVandACO && a_IsAWVandACO) {
            return -1;
        }

        // AWV non-ACO patient
        const a_IsAWVnotACO = !!a.isAwvPatient && !!a.isAcoPatient;
        const b_IsAWVnotACO = !!b.isAwvPatient && !!b.isAcoPatient;

        if (!a_IsAWVnotACO && b_IsAWVnotACO) {
            return 1;
        } else if (!b_IsAWVnotACO && a_IsAWVnotACO) {
            return -1;
        }

        return 0;
    };

    const renderCensus = () => {
        if (isMobileView()) {
            return <PatientTableMobile census={dataSource} />;
        }

        return (
            <Table
                size="large"
                rowKey={(x) => `${x.patientIntakeId ?? 0}-${x.name}`}
                pagination={false}
                dataSource={dataSource}
                expandable={{
                    indentSize: 0,
                    rowExpandable: () => isInRole(auth?.permissions, RolesAdminOrAnyProvider),
                    expandedRowRender: (censusData) => <ActionButtons census={censusData} />,
                }}
                className="census-table"
            >
                <Column<CensusDto>
                    title=""
                    key="facilityType"
                    width={75}
                    filters={[
                        { text: 'Skilled', value: FacilityTypes.SkilledNursingFacility },
                        { text: 'Not Skilled', value: 0 },
                    ]}
                    onFilter={(value, record) =>
                        value === FacilityTypes.SkilledNursingFacility ? record.facilityType === value : record.facilityType > FacilityTypes.SkilledNursingFacility
                    }
                    sorter={(a, b, direction) => sortWithNullsAtBottom(a, b, direction, 'facilityType')}
                    render={(item) => <Skilled census={item} />}
                />

                <Column<CensusDto>
                    width={120}
                    title="Room #"
                    className="text-nowrap"
                    sorter={(a, b, direction) => sortWithNullsAtBottom(a, b, direction, 'roomNumber')}
                    render={(data: CensusDto) => <EditableRoomNumber census={data} />}
                />

                <Column<CensusDto>
                    title="Scheduled"
                    key="quickNoteScheduled"
                    width={200}
                    className="text-nowrap"
                    filters={[
                        { text: 'Scheduled', value: true },
                        { text: 'Not Scheduled', value: false },
                    ]}
                    onFilter={(value, record) => {
                        const item = badges.find((x) => x.patientIntakeId === record.patientIntakeId);

                        return value ? item?.scheduledUser != null : item?.scheduledUser == null;
                    }}
                    sorter={(a, b, direction) => {
                        const itemA = badges.find((x) => x.patientIntakeId === a.patientIntakeId);
                        const itemB = badges.find((x) => x.patientIntakeId === b.patientIntakeId);

                        return sortWithNullsAtBottom(itemA, itemB, direction, 'scheduledUser');
                    }}
                    render={(data: CensusDto) => <ScheduledBadge patientIntakeId={data.patientIntakeId} />}
                />

                {/* <Column<CensusDto>
                    title="Next Visit"
                    key="name"
                    width={350}
                    sorter={(a, b) => nextVisitSorter(a, b)}
                    defaultSortOrder="ascend"
                    sortOrder="ascend"
                    render={(item) => renderVisitBadges(item)}
                    className="visit-badges"
                /> */}

                <Column<CensusDto>
                    title="Patient Name"
                    key="name"
                    width={350}
                    sorter={(a, b, direction) => sortWithNullsAtBottom(a, b, direction, 'name')}
                    render={(item) => renderPatientName(item)}
                    className="name-with-badges"
                />

                {showRequiredVisitColumns && (
                    <>
                        <Column<CensusDto>
                            title="AWV"
                            key="awvEligibilityStatus"
                            width={120}
                            sorter={(a, b, direction) => sortWithNullsAtBottom(a, b, direction, 'awvEligibilityStatus')}
                            render={(item) => renderAWV(item)}
                        />

                        <Column<CensusDto>
                            title="Visit Due"
                            key="overDueInDays"
                            sorter={(a, b, direction) => {
                                const itemA = visitDues.find((x) => x.patientIntakeId === a.patientIntakeId);
                                const itemB = visitDues.find((x) => x.patientIntakeId === b.patientIntakeId);

                                return sortWithNullsAtBottom(itemA, itemB, direction, 'overDueInDays');
                            }}
                            render={(data: CensusDto) => (
                                <VisitDueBadge admissionDate={data.admissionDate} admittedToId={data.admittedToId} patientIntakeId={data.patientIntakeId} />
                            )}
                        />

                        <Column<CensusDto>
                            title="Visit Type(s)"
                            key="visitType"
                            sorter={(a, b, direction) => {
                                const itemA = visitDues.find((x) => x.patientIntakeId === a.patientIntakeId);
                                const itemB = visitDues.find((x) => x.patientIntakeId === b.patientIntakeId);

                                return sortWithNullsAtBottom(itemA, itemB, direction, 'visitType');
                            }}
                            render={(data: CensusDto) => <VisitTypeBadge patientIntakeId={data.patientIntakeId} />}
                        />

                        <Column<CensusDto>
                            title="Provider Type"
                            key="providerVisitType"
                            filters={getEnumFilters(requiredVisitTypes).filter((x) => getProviderTypes().includes(x.value))}
                            onFilter={(value, record) => {
                                const item = visitDues.find((x) => x.patientIntakeId === record.patientIntakeId);

                                return item?.providerVisitType === value;
                            }}
                            width={150}
                            sorter={(a, b, direction) => {
                                const itemA = visitDues.find((x) => x.patientIntakeId === a.patientIntakeId);
                                const itemB = visitDues.find((x) => x.patientIntakeId === b.patientIntakeId);

                                return sortWithNullsAtBottom(itemA, itemB, direction, 'providerVisitType');
                            }}
                            render={(data: CensusDto) => <ProviderTypeBadge patientIntakeId={data.patientIntakeId} />}
                        />
                    </>
                )}

                <Column<CensusDto>
                    title="Last Visited"
                    key="last-visited"
                    width={150}
                    sorter={(a, b, direction) => {
                        const itemA = badges.find((x) => x.patientIntakeId === a.patientIntakeId);
                        const itemB = badges.find((x) => x.patientIntakeId === b.patientIntakeId);

                        return sortWithNullsAtBottom(itemA, itemB, direction, 'latestSignedNoteServiceDate');
                    }}
                    render={(data: CensusDto) => <LastVisitedBadge latestNote={latestNote} patientIntakeId={data.patientIntakeId} />}
                />
            </Table>
        );
    };

    return census.length > 0 ? (
        <>
            {renderSearch()}
            {renderCensus()}
        </>
    ) : (
        <Alert showIcon type="info" message="No patients found for this facility." />
    );
};

export default PatientTable;
